<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import type { SearchResult } from '@/lib/types';
import { formatDistance, formatPrice, formatRating } from '@/lib/format';
import CategoryIcon from '@/components/ui/CategoryIcon.vue';

/**
 * A gig card: cover on top, the facts below. Until a listing has photos the
 * cover is its category's glyph on the accent wash — a branded placeholder,
 * not an empty gray box.
 */
const props = defineProps<{ result: SearchResult }>();

const price = computed(() =>
  formatPrice(props.result.priceType, props.result.priceMin, props.result.priceMax, props.result.currency),
);
const rating = computed(() => formatRating(props.result.ratingAvg, props.result.ratingCount));
const distance = computed(() => formatDistance(props.result.distanceKm));

/** "QuickFix Plumbing" → "Q". Enough to give the row a face without a logo. */
const initial = computed(() => props.result.businessName.trim().charAt(0).toUpperCase());
</script>

<template>
  <RouterLink class="gig" :to="{ name: 'listing', params: { id: result.id } }">
    <span class="gig__cover">
      <img v-if="result.coverUrl" :src="result.coverUrl" alt="" loading="lazy" />
      <span v-else class="gig__glyph" aria-hidden="true">
        <CategoryIcon :name="result.categoryIconName" />
      </span>
      <span class="gig__cat">{{ result.categoryName }}</span>
    </span>

    <span class="gig__body">
      <span class="gig__provider">
        <span class="gig__avatar" aria-hidden="true">{{ initial }}</span>
        <span class="gig__business">
          {{ result.businessName }}<template v-if="result.city"> · {{ result.city }}</template>
        </span>
      </span>

      <span class="gig__title">{{ result.title }}</span>

      <span class="gig__meta">
        <span v-if="rating" class="gig__rating">★ {{ rating }}</span>
        <span v-else class="gig__rating gig__rating--none">No reviews yet</span>
        <span v-if="distance" class="gig__distance">{{ distance }}</span>
        <span class="gig__price">{{ price }}</span>
      </span>
    </span>
  </RouterLink>
</template>

<style scoped>
.gig {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  text-decoration: none;
  color: var(--text);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}
.gig:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: var(--border-strong);
}

.gig__cover {
  position: relative;
  display: grid;
  place-items: center;
  aspect-ratio: 16 / 10;
  background: var(--accent-weak);
  color: var(--accent);
  overflow: hidden;
}
.gig__cover img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.gig__glyph :deep(.cat-icon) { width: 44px; height: 44px; opacity: 0.85; }

.gig__cat {
  position: absolute;
  left: 0.6rem;
  bottom: 0.6rem;
  max-width: calc(100% - 1.2rem);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0.14rem 0.55rem;
  border-radius: var(--radius-pill);
  background: color-mix(in srgb, var(--surface) 88%, transparent);
  color: var(--text);
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.gig__body { display: grid; gap: 0.45rem; padding: 0.8rem 0.9rem 0.9rem; }

.gig__provider { display: flex; align-items: center; gap: 0.45rem; min-width: 0; }
.gig__avatar {
  display: grid;
  place-items: center;
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--accent-weak);
  color: var(--accent);
  font-size: 0.68rem;
  font-weight: 700;
}
.gig__business {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--muted);
  font-size: 0.84rem;
}

.gig__title {
  font-weight: 600;
  font-size: 0.98rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  /* Two lines whether the title needs them or not, so card feet line up in a rail. */
  min-height: calc(2 * 1.35em);
}

.gig__meta {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  margin-top: 0.1rem;
}
.gig__rating { font-size: 0.86rem; font-weight: 600; }
.gig__rating--none { color: var(--muted); font-weight: 400; font-size: 0.82rem; }
.gig__distance { color: var(--muted); font-size: 0.82rem; }
.gig__price { margin-left: auto; font-weight: 700; font-size: 0.95rem; }

@media (prefers-reduced-motion: reduce) {
  .gig { transition: none; }
}
</style>
